import { Directive, ElementRef, HostBinding, HostListener, inject, Input, OnInit, output, Renderer2 } from '@angular/core';

import { fromEvent, Subscription } from 'rxjs';

/**
 * @module BnResizeDirective
 * @description A directive that allows an element to be resized vertically.
 * @selector bnWebUiResize
 *
 * @input height - {number} - Defines the initial height of the element.
 * @input minHeight - {number} - Defines the minimum height the element can be resized to. Default is 100.
 * @input maxHeight - {number} - Defines the maximum height the element can be resized to. Default is 300.
 *
 * @output heightChange - {EventEmitter<number>} - Emits the new height of the element whenever it changes.
 */
@Directive({ selector: '[bnWebUiResize]' })
export class BnResizeDirective implements OnInit {
  private hostElementRef = inject(ElementRef);
  private renderer = inject(Renderer2);

  private oldY = 0;

  private handleElement!: HTMLElement;
  private grabbingSubscriptions = new Subscription();

  @Input()
  public height!: number;

  @Input()
  minHeight = 75;

  @Input()
  maxHeight = 300;

  readonly heightChange = output<number>();

  @HostBinding('class.is-resizing') isGrabbing = false;

  @HostListener('document:mouseup')
  onMouseUp(): void {
    this.isGrabbing = false;
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.isGrabbing) {
      return;
    }

    // if (!this.handleElement.matches(':hover')) {
    //   this.isGrabbing = false;
    // }

    const proposedHeight = this.height - (event.clientY - this.oldY);

    if (proposedHeight >= this.minHeight && proposedHeight <= this.maxHeight) {
      this.height -= event.clientY - this.oldY;
      this.heightChange.emit(this.height);
      this.oldY = event.clientY;
    }
  }

  public ngOnInit(): void {
    this.renderer.addClass(this.hostElementRef.nativeElement, 'position-relative');

    this.handleElement = this.renderer.createElement('div') as HTMLElement;
    this.renderer.setAttribute(this.handleElement, 'class', 'resizable-handle');
    this.renderer.appendChild(this.hostElementRef.nativeElement, this.handleElement);

    this.grabbingSubscriptions.add(
      fromEvent(this.handleElement, 'mousedown').subscribe((event) => {
        this.isGrabbing = true;
        this.oldY = (event as MouseEvent).clientY;
      }),
    );
  }
}
